import { createContext, useContext, useMemo } from "react";
import { trpc } from "../trpc";
import MerchantContext from "./MerchantContext";

export type PipelinePosition = {
  id: string;
  stage: string;
  status: string;
  displayName: string;
  isSystem: boolean;
  order: number;
};

type PipelineData = {
  new?: PipelinePosition[];
  pending?: PipelinePosition[];
  success?: PipelinePosition[];
  failure?: PipelinePosition[];
};

type PipelineContextType = {
  pipelineData: PipelineData | undefined;
  allPositions: PipelinePosition[];
  defaultPipelinePositions: PipelineData | undefined;
  sourcePropertiesPipelinePositions: PipelineData | undefined;
  maintenancePipelinePositions: PipelineData | undefined;
  generalPipelinePositions: PipelineData | undefined;
  collectionsPipelinePositions: PipelineData | undefined;
  isLoading: boolean;
};

const PipelineContext = createContext<PipelineContextType>({
  pipelineData: undefined,
  allPositions: [],
  defaultPipelinePositions: undefined,
  sourcePropertiesPipelinePositions: undefined,
  maintenancePipelinePositions: undefined,
  generalPipelinePositions: undefined,
  collectionsPipelinePositions: undefined,
  isLoading: true,
});

const PipelineContextProvider = ({ children }: React.PropsWithChildren): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);

  // Fetch pipeline data when merchantId is available
  const { data: pipeline, isLoading } = trpc.pipeline.get.useQuery(
    { merchantId },
    {
      enabled: !!merchantId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  // Fetch positions from all pipelines for complete coverage
  const { data: defaultPipelinePositions } = trpc.pipeline.getPositions.useQuery(
    { merchantId: merchantId || "", pipelineId: "default" },
    {
      enabled: !!merchantId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  const { data: sourcePropertiesPipelinePositions } = trpc.pipeline.getPositions.useQuery(
    { merchantId: merchantId || "", pipelineId: "sourceproperties" },
    {
      enabled: !!merchantId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  const { data: maintenancePipelinePositions } = trpc.pipeline.getPositions.useQuery(
    { merchantId: merchantId || "", pipelineId: "maintenance" },
    {
      enabled: !!merchantId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  const { data: generalPipelinePositions } = trpc.pipeline.getPositions.useQuery(
    { merchantId: merchantId || "", pipelineId: "general" },
    {
      enabled: !!merchantId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  const { data: collectionsPipelinePositions } = trpc.pipeline.getPositions.useQuery(
    { merchantId: merchantId || "", pipelineId: "collections" },
    {
      enabled: !!merchantId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  // Memoize flattened positions from all pipelines to avoid recalculation on every render
  const allPositions = useMemo(() => {
    const allPositionsMap = new Map<string, PipelinePosition>();

    const flattenPositions = (data: PipelineData | undefined): PipelinePosition[] => {
      if (!data) return [];
      const positions: PipelinePosition[] = [];
      if (data.new) positions.push(...data.new);
      if (data.pending) positions.push(...data.pending);
      if (data.success) positions.push(...data.success);
      if (data.failure) positions.push(...data.failure);
      return positions;
    };

    // Add positions from all pipelines to the map (deduplicates by id)
    flattenPositions(defaultPipelinePositions).forEach((position) => {
      if (!allPositionsMap.has(position.id)) {
        allPositionsMap.set(position.id, position);
      }
    });

    flattenPositions(sourcePropertiesPipelinePositions).forEach((position) => {
      if (!allPositionsMap.has(position.id)) {
        allPositionsMap.set(position.id, position);
      }
    });

    flattenPositions(maintenancePipelinePositions).forEach((position) => {
      if (!allPositionsMap.has(position.id)) {
        allPositionsMap.set(position.id, position);
      }
    });

    flattenPositions(generalPipelinePositions).forEach((position) => {
      if (!allPositionsMap.has(position.id)) {
        allPositionsMap.set(position.id, position);
      }
    });

    flattenPositions(collectionsPipelinePositions).forEach((position) => {
      if (!allPositionsMap.has(position.id)) {
        allPositionsMap.set(position.id, position);
      }
    });

    return Array.from(allPositionsMap.values());
  }, [
    defaultPipelinePositions,
    sourcePropertiesPipelinePositions,
    maintenancePipelinePositions,
    generalPipelinePositions,
    collectionsPipelinePositions,
  ]);

  return (
    <PipelineContext.Provider
      value={{
        pipelineData: pipeline,
        allPositions,
        defaultPipelinePositions,
        sourcePropertiesPipelinePositions,
        maintenancePipelinePositions,
        generalPipelinePositions,
        collectionsPipelinePositions,
        isLoading,
      }}
    >
      {children}
    </PipelineContext.Provider>
  );
};

export const usePipeline = () => {
  const context = useContext(PipelineContext);
  if (!context) {
    throw new Error("usePipeline must be used within a PipelineProvider");
  }
  return context;
};

const KNOWN_PIPELINE_IDS = ["default", "sourceproperties", "maintenance", "general", "collections"];

// Helper function to get positions for a specific pipeline (including custom pipelines)
export const usePipelinePositions = (pipelineId?: string): PipelinePosition[] => {
  const context = useContext(PipelineContext);
  const { merchantId } = useContext(MerchantContext);
  if (!context) {
    throw new Error("usePipelinePositions must be used within a PipelineProvider");
  }

  const isCustomPipeline = !!pipelineId && !KNOWN_PIPELINE_IDS.includes(pipelineId);
  const { data: customPipelineData } = trpc.pipeline.getPositions.useQuery(
    { merchantId: merchantId || "", pipelineId: pipelineId || "" },
    {
      enabled: !!merchantId && !!pipelineId && isCustomPipeline,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  return useMemo(() => {
    if (!pipelineId) return [];

    if (isCustomPipeline) {
      if (!customPipelineData) return [];
      const positions: PipelinePosition[] = [];
      if (customPipelineData.new) positions.push(...customPipelineData.new);
      if (customPipelineData.pending) positions.push(...customPipelineData.pending);
      if (customPipelineData.success) positions.push(...customPipelineData.success);
      if (customPipelineData.failure) positions.push(...customPipelineData.failure);
      return positions;
    }

    let pipelineData: PipelineData | undefined;
    switch (pipelineId) {
      case "default":
        pipelineData = context.defaultPipelinePositions;
        break;
      case "sourceproperties":
        pipelineData = context.sourcePropertiesPipelinePositions;
        break;
      case "maintenance":
        pipelineData = context.maintenancePipelinePositions;
        break;
      case "general":
        pipelineData = context.generalPipelinePositions;
        break;
      case "collections":
        pipelineData = context.collectionsPipelinePositions;
        break;
      default:
        return [];
    }

    if (!pipelineData) return [];

    const positions: PipelinePosition[] = [];
    if (pipelineData.new) positions.push(...pipelineData.new);
    if (pipelineData.pending) positions.push(...pipelineData.pending);
    if (pipelineData.success) positions.push(...pipelineData.success);
    if (pipelineData.failure) positions.push(...pipelineData.failure);

    return positions;
  }, [
    pipelineId,
    isCustomPipeline,
    customPipelineData,
    context.defaultPipelinePositions,
    context.sourcePropertiesPipelinePositions,
    context.maintenancePipelinePositions,
    context.generalPipelinePositions,
    context.collectionsPipelinePositions,
  ]);
};

export default PipelineContext;
export { PipelineContextProvider };
